export type AgentHonestyInput = {
  agent_id?: string | null;
  id?: string | null;
  name?: string | null;
  display_name?: string | null;
  app_id?: string | null;
  status?: string | null;
  deployment_stage?: string | null;
  leftover?: boolean | null;
  probe_or_smoke?: boolean | null;
  honestly_active?: boolean | null;
  honestly_healthy?: boolean | null;
  health_status?: { is_healthy?: boolean | null } | null;
  description?: string | null;
  honesty_stage?: string | null;
  terminal_stub?: boolean | null;
  volcano_workflow?: unknown;
  volcano_config?: { workflows?: unknown } | null;
  step_count?: number | null;
  probe?: boolean | null;
  is_probe?: boolean | null;
  smoke?: boolean | null;
  flag?: string | null;
  flags?: string | null;
  kind?: string | null;
};

export type ExecutionHonestyRow = {
  execution_id?: string | null;
  _id?: string | null;
  agent_id?: string | null;
  agent_name?: string | null;
  status?: string | null;
  executed_at?: string | Date | null;
  metrics?: { cost_usd?: number | null; estimated_cost?: number | null } | null;
};

const CORE_APP_ID = 'app_5UxnQWMZZ2Fa';

const LEFTOVER_ID = /agenticfarm-sentinel|nampo|testbench(-runs)?|qa-soil(-health)?|ecosoil|(^|-)lc-|probe-vc-|__prod\d+_smoke|smoke_recovery|farmspace-weather|farmspace-geo-write|^trekker$/i;
const LEFTOVER_NAME = /agenticfarm sentinel|nampo|testbench(-runs)?|qa-soil(-health)?|ecosoil|(^|\s)lc-|probe-vc-|__prod\d+_smoke|smoke_recovery|farmspace-weather|farmspace-geo-write|trekker demo/i;

function haystack(agent: AgentHonestyInput | null | undefined) {
  return {
    id: String(agent?.agent_id || agent?.id || ''),
    name: String(agent?.name || agent?.display_name || ''),
  };
}

export function isProbeOrSmokeAgent(agent: AgentHonestyInput | null | undefined): boolean {
  if (agent?.probe_or_smoke === true || agent?.probe === true || agent?.is_probe === true || agent?.smoke === true) {
    return true;
  }
  const { id, name } = haystack(agent);
  if (/probe-vc-|__prod\d+_smoke|smoke_recovery/i.test(`${id} ${name}`)) return true;
  return /\b(probe|smoke)\b/i.test(`${agent?.flag || ''} ${agent?.flags || ''} ${agent?.kind || ''}`);
}

export function isLeftoverCoreAgent(agent: AgentHonestyInput | null | undefined): boolean {
  const { id, name } = haystack(agent);
  if (/^trekker-sync$/i.test(id) || /trekker sync/i.test(name)) return false;
  if (agent?.leftover === true) return true;
  return LEFTOVER_ID.test(id) || LEFTOVER_NAME.test(name);
}

export function isTerminalStubAgent(agent: AgentHonestyInput | null | undefined): boolean {
  if (!agent) return false;
  if (agent.terminal_stub === true || agent.honesty_stage === 'stub') return true;

  const desc = String(agent.description || '');
  const { id, name } = haystack(agent);
  const admits = /terminal stub|\bstub only\b|placeholder workflow|not implemented/i.test(`${id} ${name} ${desc}`);

  const steps = getWorkflowSteps(agent).filter((step) => step && typeof step === 'object') as Array<Record<string, unknown>>;
  if (!steps.length) return admits;

  const stubSteps = steps.filter((step) => {
    const provider = String(step.provider || '').toLowerCase();
    const stepName = String(step.name || step.id || '').toLowerCase();
    return provider === 'transform' && /return_result/.test(stepName);
  });
  const onlyReturnResult = stubSteps.length === steps.length;
  const identicalPair = stubSteps.length >= 2 && stubSteps.every((step) => (
    String(step.name || '').toLowerCase() === String(stubSteps[0].name || '').toLowerCase()
    && String(step.provider || '') === String(stubSteps[0].provider || '')
  ));

  return onlyReturnResult && (identicalPair || admits);
}

export function isHonestProduction(agent: AgentHonestyInput | null | undefined): boolean {
  return String(agent?.deployment_stage || '') === 'production'
    && !isProbeOrSmokeAgent(agent)
    && !isLeftoverCoreAgent(agent)
    && !isTerminalStubAgent(agent);
}

export function isHonestlyActive(agent: AgentHonestyInput | null | undefined): boolean {
  if (!agent) return false;
  if (typeof agent.honestly_active === 'boolean') return agent.honestly_active;
  if (isLeftoverCoreAgent(agent)) return false;
  if (isProbeOrSmokeAgent(agent)) return false;
  if (isTerminalStubAgent(agent)) return false;
  if (String(agent.deployment_stage || '').toLowerCase() !== 'production') return false;
  const status = String(agent.status || '').toLowerCase();
  if (status && status !== 'active') return false;
  return true;
}

export function isHonestlyHealthy(agent: AgentHonestyInput | null | undefined): boolean {
  if (typeof agent?.honestly_healthy === 'boolean') return agent.honestly_healthy;
  return agent?.health_status?.is_healthy === true
    && !isProbeOrSmokeAgent(agent)
    && !isLeftoverCoreAgent(agent)
    && !isTerminalStubAgent(agent);
}

export function agentStageLabel(agent: AgentHonestyInput | null | undefined): string {
  if (isProbeOrSmokeAgent(agent)) return 'probe/smoke';
  if (isLeftoverCoreAgent(agent)) return 'leftover';
  if (isTerminalStubAgent(agent)) {
    return String(agent?.deployment_stage || '') === 'production'
      ? 'production (stub)'
      : 'stub';
  }
  return String(agent?.deployment_stage || 'draft');
}

export function agentHealthLabel(agent: AgentHonestyInput | null | undefined): string {
  if (isProbeOrSmokeAgent(agent)) return 'Probe/smoke — not production-healthy';
  if (isLeftoverCoreAgent(agent)) return 'Leftover — not Core-healthy';
  if (agent?.health_status?.is_healthy === true) return 'Healthy';
  if (agent?.health_status?.is_healthy === false) return 'Unhealthy';
  return 'Health unknown';
}

function extractSteps(workflow: unknown): unknown[] {
  if (Array.isArray(workflow)) return workflow.filter(Boolean);
  if (workflow && typeof workflow === 'object' && Array.isArray((workflow as { steps?: unknown[] }).steps)) {
    return ((workflow as { steps: unknown[] }).steps || []).filter(Boolean);
  }
  return [];
}

export function getWorkflowSteps(agent: AgentHonestyInput | null | undefined): unknown[] {
  if (!agent) return [];

  const workflows = agent.volcano_config?.workflows;
  if (Array.isArray(workflows)) {
    return workflows.flatMap((workflow) => extractSteps(workflow));
  }
  if (workflows && typeof workflows === 'object') {
    return Object.values(workflows).flatMap((workflow) => extractSteps(workflow));
  }

  return extractSteps(agent.volcano_workflow);
}

export function countWorkflowSteps(agent: AgentHonestyInput | null | undefined): number {
  const hasWorkflowSurface = agent?.volcano_workflow != null || agent?.volcano_config?.workflows != null;
  if (hasWorkflowSurface) {
    return getWorkflowSteps(agent).length;
  }
  if (typeof agent?.step_count === 'number' && Number.isFinite(agent.step_count)) {
    return agent.step_count;
  }
  return getWorkflowSteps(agent).length;
}

export function countHonestActive(agents: AgentHonestyInput[] | null | undefined): number {
  return (agents || []).filter(isHonestlyActive).length;
}

function executionDedupeKey(row: ExecutionHonestyRow | null | undefined): string {
  if (!row) return '';
  if (row.execution_id) return `id:${row.execution_id}`;
  const ts = row.executed_at ? new Date(row.executed_at).toISOString() : '';
  return `fp:${row.agent_id || row.agent_name || ''}|${ts}|${row.status || ''}`;
}

function nearDuplicateKey(row: ExecutionHonestyRow): string {
  const ts = row.executed_at ? new Date(row.executed_at).toISOString().slice(0, 19) : '';
  const status = String(row.status || '').replace(/failed/, 'failure');
  return `${row.agent_id || ''}|${ts}|${status}`;
}

export function dedupeExecutions<T extends ExecutionHonestyRow>(rows: T[] | null | undefined): T[] {
  const list = Array.isArray(rows) ? rows : [];
  const seenIds = new Set<string>();
  const seenNear = new Set<string>();
  const out: T[] = [];
  for (const row of list) {
    const idKey = executionDedupeKey(row);
    const nearKey = nearDuplicateKey(row);
    if ((idKey && seenIds.has(idKey)) || (nearKey && seenNear.has(nearKey))) continue;
    if (idKey) seenIds.add(idKey);
    if (nearKey) seenNear.add(nearKey);
    out.push(row);
  }
  return out;
}

export function measureFailed(rows: ExecutionHonestyRow[] | null | undefined): {
  measured: boolean;
  failed: number | null;
  total: number;
} {
  const unique = dedupeExecutions(rows);
  if (!unique.length) {
    return { measured: false, failed: null, total: 0 };
  }
  const failed = unique.filter((row) => {
    const status = String(row.status || '').toLowerCase();
    return status === 'failure' || status === 'failed' || status === 'error' || status === 'timeout';
  }).length;
  return { measured: true, failed, total: unique.length };
}

export function observeCoverageNote(rows: ExecutionHonestyRow[] | null | undefined): string | null {
  const unique = dedupeExecutions(rows);
  if (!unique.length) {
    return 'No recorded executions in this window. Missing history is unmeasured, not proof that only one agent ran.';
  }
  const agents = new Set(unique.map((row) => row.agent_name || row.agent_id || 'unknown'));
  if (agents.size === 1) {
    const only = [...agents][0];
    return `Observe is only seeing recorded history for ${only}. Other agents may have run without a persisted execution row.`;
  }
  return null;
}

export function isCoreAppId(appId: string | null | undefined): boolean {
  return String(appId || '') === CORE_APP_ID;
}
